import { useState } from 'react';
import { CalendarClockIcon } from 'lucide-react';
import { app, xhs } from '@/lib/bridge';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { dateFromXhsId, displayImageUrl, extractUrl, feedBody, isShortLink, noteRefFromUrl, parseFeed, userIdFromProfileUrl } from '@/lib/xhsLinks';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/skeletons';
import { Card } from '@/components/ui/card';
import { CARD, HINT } from '@/components/ui';
import { ErrorCard, PasteButton, readClipboardText, ToolNotices, useXhsTool, xhsErrorText } from './common';

/**
 * 薯薯 · 查询小红书账号注册时间（`/shushu/xhs/account-created?accountId=`）。
 *
 * 移植自搬小书 `mobile/tools/account-created`（去掉额度扣费）：小红书 user_id 前 8 位 hex 是创建时的 Unix 秒。
 * - 用户主页链接：路径里就有 user_id，本地直接算；
 * - 笔记链接：用当前账号调 feed 拿作者 user_id（顺便拿昵称头像）。
 */

type Result = { userId: string; nickname: string; avatar: string; date: Date };

function formatDate(d: Date) {
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

export function ShushuXhsAccountCreatedPage() {
  const tool = useXhsTool();
  const { account, isNative, outdated, loaded } = tool;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  useGroupedSurface();
  useAppReady(loaded);

  const lookup = async (raw: string) => {
    const url = extractUrl(raw) ?? raw.trim();
    if (!url) {
      setError('请粘贴小红书用户主页或笔记链接');
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const full = isShortLink(url) ? (await app.resolveUrl(url)).url : url;
      let found: Omit<Result, 'date'> | null = null;
      const profileId = userIdFromProfileUrl(full);
      if (profileId) {
        found = { userId: profileId, nickname: '', avatar: '' };
      } else {
        const ref = noteRefFromUrl(full);
        if (!ref) {
          setError('无法识别这个链接，请粘贴小红书用户主页或笔记链接');
          return;
        }
        if (!tool.usable || !account) {
          setError('查笔记作者要用当前小红书账号的登录态，请先登录');
          return;
        }
        const { data } = await xhs.request(account.id, { method: 'POST', path: '/api/sns/web/v1/feed', body: feedBody(ref) });
        found = parseFeed(data, ref.noteId)?.author ?? null;
        if (!found) {
          setError('没能拿到这篇笔记的作者信息，链接可能已失效');
          return;
        }
      }
      const date = dateFromXhsId(found.userId);
      if (!date) {
        setError('无法从这个账号 ID 推算注册时间');
        return;
      }
      setResult({ ...found, date });
      void app.haptic('success');
    } catch (e) {
      setError(xhsErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const paste = async () => {
    try {
      await lookup(await readClipboardText('剪贴板是空的，请先在小红书里复制主页或笔记链接'));
    } catch (e) {
      setError(xhsErrorText(e));
    }
  };

  const avatar = displayImageUrl(result?.avatar);
  const disabled = busy || !isNative || outdated;

  return (
    <PageShell title="查询注册时间" description="粘贴小红书主页或笔记链接">
      {!loaded ? <PageSkeleton kind="linkTool" /> : null}
      {loaded && (!isNative || outdated) ? <ToolNotices {...tool} /> : null}

      {loaded ? (
        <div className="flex flex-col items-center gap-3">
          <PasteButton
            title="粘贴链接并查询"
            subtitle="支持笔记或用户主页"
            busyText="正在解析…"
            busy={busy}
            disabled={disabled}
            onClick={() => void paste()}
          />
          <p className={HINT}>按账号 ID 推算，精确到天</p>
        </div>
      ) : null}

      {result ? (
        <Card className={CARD}>
          <div className="flex items-center gap-3">
            <div className="bg-muted text-muted-foreground flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-full">
              {avatar ? (
                <img className="size-full object-cover" src={avatar} alt="" referrerPolicy="no-referrer" />
              ) : (
                <CalendarClockIcon className="size-6" />
              )}
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-body font-semibold">{result.nickname || '账号注册时间'}</h2>
              <p className="mt-0.5 text-title3 font-bold tabular-nums">{formatDate(result.date)}</p>
            </div>
          </div>
          <p className={`${HINT} break-all select-text`}>账号 ID：{result.userId}</p>
        </Card>
      ) : null}

      {error ? <ErrorCard title="查询失败" message={error} /> : null}
    </PageShell>
  );
}
